import { useSyncExternalStore } from 'react'

import { shouldBypassAuthForE2E } from '~/shell/config'

import { sessionClaimsHolder } from './sessionClaims'

/**
 * The session's tenancy identity, as the bootstrap needs it.
 *
 * `customerClaim` is the single `customer` value carried on the auth token (see
 * the **Customer claim** glossary entry in `GLOSSARY.md`); `null` for a
 * `regard_admin`, who carries no customer and may access any of them.
 */
export interface Identity {
  isAdmin: boolean
  customerClaim: string | null
}

/**
 * The identity the E2E bypass runs as: a `regard_admin` with no customer, which is the session the
 * bootstrap resolves a default customer for.
 *
 * The bypass replaces Auth0 entirely, so there is no token to read and the claims resolve to none —
 * an identity that would land the smoke run on the no-access page. Stating the bypass's identity here
 * keeps that a property of the bypass rather than of an unreadable token, which must stay
 * unauthorized.
 */
const E2E_BYPASS_IDENTITY: Identity = { isAdmin: true, customerClaim: null }

/**
 * Resolves the session's tenancy identity from the claims read off its access token.
 *
 * The one seam every consumer reads identity through.
 *
 * Subscribed rather than read once, because the analytics bridges mount outside the guarded tree and
 * so render before `RequireAuth` has resolved the claims (see `sessionClaimsHolder`). A consumer
 * inside that tree always reads a resolved identity and never observes an update.
 */
export const useIdentity = (): Identity => {
  const claims = useSyncExternalStore(
    sessionClaimsHolder.subscribe,
    sessionClaimsHolder.get,
  )

  if (shouldBypassAuthForE2E) {
    return E2E_BYPASS_IDENTITY
  }

  return { isAdmin: claims.isAdmin, customerClaim: claims.customer }
}
